import { forwardRef, type InputHTMLAttributes } from "react";

import { cx } from "./cx";

/** Shared by Input and Select. */
export const CONTROL =
  "block w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle " +
  "focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:opacity-60";

/** The roomier control of the redesigned forms (domains, contacts): 10px radius, 12px padding. */
export const CONTROL_LG =
  "block w-full rounded-[10px] border border-border bg-surface p-3 text-sm text-fg outline-none placeholder:text-fg-subtle " +
  "focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:bg-muted aria-invalid:border-[#d6342f]";

/** The class of a text control; for a `<select>` or `<textarea>` that should look like an `Input`. */
export function controlClass(size: "md" | "lg" = "md", className?: string): string {
  return cx(size === "lg" ? CONTROL_LG : CONTROL, className);
}

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  size?: "md" | "lg";
  /** Marks the control invalid (as a Field's `aria-invalid` does). */
  invalid?: boolean;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, size = "md", invalid, ...rest },
  ref,
) {
  const bad = Boolean(invalid) || rest["aria-invalid"] === true || rest["aria-invalid"] === "true";
  return (
    <input ref={ref} className={controlClass(size, className)} {...rest} aria-invalid={bad || undefined} />
  );
});
